import React, { useRef, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Printer, ArrowLeft, Download } from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getHBLByID } from '@/redux/Booking/actions';
import { Button } from '@/components/ui/button';

const HBLPrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { bookingId: bookingIdFromState, FILENUMBER } = location.state || {};
    const searchParams = new URLSearchParams(location.search);
    const bookingIdFromQuery = searchParams.get("bookingId");
    const bookingIdParsed =
        bookingIdFromQuery != null && bookingIdFromQuery !== ""
            ? Number(bookingIdFromQuery) || bookingIdFromQuery
            : null;
    const bookingId =
        bookingIdFromState != null && bookingIdFromState !== ""
            ? bookingIdFromState
            : bookingIdParsed;
    const { MasterCompanyDetails } = useSelector((state) => state.common);

    const { id: idQuote } = useParams();
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const documentRef = useRef();
    // AR/AP Charges State
    const [arCharges, setArCharges] = useState([]);
    const [apCharges, setApCharges] = useState([]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                if (bookingId && bookingId !== 0) {
                    await _getBooking();

                }
            } catch (error) {
                console.error("Error initializing booking:", error);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [bookingId]);

    const _getBooking = async () => {
        try {
            let response;
            if (typeof bookingId === 'number' || (typeof bookingId === 'string' && bookingId !== '0')) {
                response = await getHBLByID(bookingId);
                console.log('HBLdata:', response);
                if (response?.data) {
                    const mappedData = {
                        ...response.data,
                        ContainersCargoModel: (response.data.ContainersCargoModel || []).map(cargo => ({
                            MARKS_NO: cargo.marksAndNumbers || cargo.MARKS_NO || "",
                            CONTAINER_NUMBER: cargo.containerNo || cargo.CONTAINER_NUMBER || "",
                            SEAL_NUMBER: cargo.sealNo || cargo.SEAL_NUMBER || "",
                            CONT_SIZE: cargo.containerSize || cargo.CONT_SIZE || "",
                            CONT_TYPE: cargo.containerType || cargo.CONT_TYPE || "",
                            DESCRIPTIONS: cargo.description || cargo.DESCRIPTIONS || "",
                            SCHEDULE_B: cargo.scheduleB || cargo.SCHEDULE_B || "",
                            NO_PACKAGE: cargo.numberOfPackages || cargo.NO_PACKAGE || 0,
                            PACKAGE_CODE: cargo.packageCode || cargo.PACKAGE_CODE || "",
                            PACKAGE_DESCRIPTION: cargo.packageDescription || cargo.PACKAGE_DESCRIPTION || "",
                            HAZ_CODE: cargo.hazCode || cargo.HAZ_CODE || "",
                            KGS: cargo.weightKg || cargo.KGS || "",
                            LBS: cargo.weightLb || cargo.LBS || "",
                            CBM: cargo.measurementCbm || cargo.CBM || "",
                        }))
                    };

                    setArCharges(response?.data?.hblArCharges || []);
                    setFormData(mappedData);
                }
            } else if (bookingId && typeof bookingId === 'object') {
                setFormData(bookingId);
            }
        } catch (error) {
            console.error("Error fetching booking:", error);
        }
    };

    useEffect(() => {
        if (!documentRef.current) return;

        const images = documentRef.current.querySelectorAll("img");
        images.forEach(img => {
            img.crossOrigin = "anonymous";
        });
    }, []);

    const downloadPDF = async () => {
        if (!documentRef.current) return;

        const watermark = documentRef.current.querySelector(".watermark");
        if (watermark) watermark.style.display = "none";

        try {
            const images = documentRef.current.querySelectorAll("img");
            await Promise.all(
                Array.from(images).map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise(resolve => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    });
                })
            );

            const fullCanvas = await html2canvas(documentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: false,
                backgroundColor: "#ffffff"
            });
            const fullImgData = fullCanvas.toDataURL("image/png");

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4"
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const padding = 5;
            const usableWidth = pdfWidth - padding * 2;
            const usableHeight = pdfHeight - padding * 2;

            const scaledFullHeight = (fullCanvas.height * usableWidth) / fullCanvas.width;

            // Single-page case — render entire image at top, done
            if (scaledFullHeight <= usableHeight) {
                pdf.addImage(fullImgData, "PNG", padding, padding, usableWidth, scaledFullHeight);
                pdf.save(`HOUSE_BILL_OF_LADING_${formData?.HBLNumber || "Document"}.pdf`);
                return;
            }

            // Multi-page case — capture header separately and repeat on every page
            const headerEl = documentRef.current.querySelector(".hbl-print-header-block");
            let headerImgData = null;
            let scaledHeaderHeight = 0;

            if (headerEl) {
                const headerCanvas = await html2canvas(headerEl, {
                    scale: 2,
                    useCORS: true,
                    allowTaint: false,
                    backgroundColor: "#ffffff",
                });
                headerImgData = headerCanvas.toDataURL("image/png");
                scaledHeaderHeight = (headerCanvas.height * usableWidth) / headerCanvas.width;
            }

            const contentPerPage = scaledHeaderHeight > 0
                ? usableHeight - scaledHeaderHeight
                : usableHeight;

            const pageCount = scaledHeaderHeight > 0
                ? 1 + Math.ceil((scaledFullHeight - usableHeight) / contentPerPage)
                : Math.ceil(scaledFullHeight / usableHeight);

            for (let i = 0; i < pageCount; i++) {
                if (i > 0) pdf.addPage();

                if (i === 0 || !headerImgData) {
                    const position = padding - (i * usableHeight);
                    pdf.addImage(fullImgData, "PNG", padding, position, usableWidth, scaledFullHeight);
                } else {
                    const position = padding - (i * contentPerPage);
                    pdf.addImage(fullImgData, "PNG", padding, position, usableWidth, scaledFullHeight);

                    pdf.setFillColor(255, 255, 255);
                    pdf.rect(0, 0, pdfWidth, padding + scaledHeaderHeight, "F");

                    pdf.addImage(headerImgData, "PNG", padding, padding, usableWidth, scaledHeaderHeight);
                }
            }

            pdf.save(`HOUSE_BILL_OF_LADING_${formData?.HBLNumber || "Document"}.pdf`);

        } catch (err) {
            console.error("PDF Error:", err);
            alert("PDF generation failed. Please try again.");
        } finally {
            if (watermark) watermark.style.display = "flex";
        }
    };

    const handlePrint = () => {
        if (!documentRef.current) return;

        const printContent = documentRef.current.innerHTML;

        const inlinedCss = Array.from(document.styleSheets)
            .map((sheet) => {
                try {
                    return Array.from(sheet.cssRules || [])
                        .map((rule) => rule.cssText)
                        .join("\n");
                } catch (err) {
                    return sheet.href
                        ? `@import url("${sheet.href}");`
                        : "";
                }
            })
            .join("\n");

        const linkFallback = Array.from(
            document.querySelectorAll('link[rel="stylesheet"]')
        )
            .map((node) => node.outerHTML)
            .join("\n");

        const rootEl = document.documentElement;
        const rootStyles = window.getComputedStyle(rootEl);
        const customProps = [];
        for (let i = 0; i < rootStyles.length; i++) {
            const name = rootStyles[i];
            if (name.startsWith("--")) {
                customProps.push(`${name}: ${rootStyles.getPropertyValue(name).trim()};`);
            }
        }
        const rootVarsCss = `:root { ${customProps.join(" ")} }`;

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        iframe.setAttribute("aria-hidden", "true");
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`<!DOCTYPE html>
<html>
<head>
    <title>BILL OF LADING - ${formData?.HBLNumber || "Document"}</title>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <style id="__hbl_root_vars">${rootVarsCss}</style>
    <style id="__hbl_app_css">${inlinedCss}</style>
    ${linkFallback}
    <style id="__hbl_print_overrides">
        @page { size: A4; margin: 0; }
        html, body {
            margin: 0;
            padding: 0;
            background: #fff;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        .hbl-print-doc {
            width: 210mm !important;
            min-height: 297mm !important;
            padding: 8mm !important;
            margin: 0 !important;
            box-shadow: none !important;
            background: #fff !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
        }
        .hbl-print-doc *,
        .hbl-print-doc *::before,
        .hbl-print-doc *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        .watermark { display: none !important; }
    </style>
</head>
<body>
    <div class="hbl-print-doc" style="font-family: Arial, sans-serif;">
        ${printContent}
    </div>
</body>
</html>`);
        iframeDoc.close();

        const triggerPrint = () => {
            try {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
            } catch (err) {
                console.error("Print failed:", err);
            }
            const cleanup = () => {
                if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
            };
            if (iframe.contentWindow) {
                iframe.contentWindow.addEventListener("afterprint", cleanup);
            }
            setTimeout(cleanup, 60000);
        };

        const waitForLoad = () => {
            const win = iframe.contentWindow;
            const doc = win.document;
            const images = Array.from(doc.images || []);
            const imagePromises = images.map((img) => {
                if (img.complete) return Promise.resolve();
                return new Promise((res) => {
                    img.onload = res;
                    img.onerror = res;
                });
            });
            const fontPromise = doc.fonts && doc.fonts.ready
                ? doc.fonts.ready
                : Promise.resolve();
            Promise.all([fontPromise, ...imagePromises]).then(() => {
                setTimeout(triggerPrint, 100);
            });
        };

        if (iframe.contentWindow.document.readyState === "complete") {
            waitForLoad();
        } else {
            iframe.onload = waitForLoad;
        }
    };


    // Start Freight Chages code 
    const normalizeCharges = (arCharges, apCharges) => {
        const ar = arCharges?.map((c) => ({
            chargeCode: c.chargeCode,
            description: c.description,
            ppcc: c.ppcc,
            amount: Number(c.sellRate || 0),
        }));

        const ap = apCharges?.map((c) => ({
            chargeCode: c.chargeCode,
            description: c.description,
            ppcc: c.ppcc,
            amount: Number(c.buyRate || 0),
        }));

        return [...ar, ...ap];
    };

    const groupCharges = (charges) => {
        return Object.values(
            charges.reduce((acc, item) => {
                const key = item.chargeCode?.trim();

                if (!acc[key]) {
                    acc[key] = {
                        chargeCode: item.chargeCode,
                        description: item.description,
                        prepaid: 0,
                        collect: 0,
                        total: 0,
                    };
                }

                if (item.ppcc === "P") {
                    acc[key].prepaid += item.amount;
                } else if (item.ppcc === "C") {
                    acc[key].collect += item.amount;
                }

                acc[key].total += item.amount;

                return acc;
            }, {})
        );
    };
    const calculateGrandTotals = (groupedData) => {
        return groupedData.reduce(
            (acc, item) => {
                acc.prepaid += item.prepaid || 0;
                acc.collect += item.collect || 0;
                return acc;
            },
            { prepaid: 0, collect: 0 }
        );
    };

    const [groupedCharges, setGroupedCharges] = useState([]);
    const [totals, setTotals] = useState({ prepaid: 0, collect: 0 });

    useEffect(() => {
        const allCharges = normalizeCharges(arCharges, apCharges);
        const grouped = groupCharges(allCharges);
        const totalValues = calculateGrandTotals(grouped);
        setGroupedCharges(grouped);
        setTotals(totalValues);
        console.log(grouped);
        console.log(totalValues);
    }, [arCharges, apCharges]);


    // End Freight Chages code 

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).isValid() ? moment(dateString).format('DD MMM YYYY') : '';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-foreground">Loading house bill of lading data...</p>
                </div>
            </div>
        );
    }

    if (!formData) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load house bill of lading data</p>
                    <button
                        onClick={() => navigate('/hbl', { state: { bookingId: bookingId, FILENUMBER: FILENUMBER } })}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to HBL
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-200 print:bg-white text-gray-900">
            {/* Action Bar */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex flex-col sm:flex-row items-center gap-2 sm:gap-0 sm:justify-between">
                    <button
                        onClick={() =>
                            navigate('/hbl', {
                                state: {
                                    bookingId: bookingId,
                                    FILENUMBER: FILENUMBER
                                },
                            })
                        }
                        className="w-full sm:w-auto flex items-center justify-center sm:justify-start gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to HBL
                    </button>
                    <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                        <button
                            onClick={downloadPDF}
                            data-download-btn
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300 text-sm sm:text-base"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download PDF</span>
                            <span className="sm:hidden">Download</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium text-sm sm:text-base"
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>

            <div className="hbl-print-stage mx-auto px-2 sm:px-4 py-4 sm:py-6">
                <div
                    ref={documentRef}
                    className="hbl-print-doc bg-white shadow-lg print:shadow-none mx-auto"
                    style={{
                        fontFamily: "Arial, sans-serif",
                        width: "210mm",
                        minHeight: "297mm",
                        padding: "8mm",
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <style>{`
                        @page {
                            size: A4;
                            margin: 0;
                        }

                        .hbl-print-stage {
                            overflow-x: auto;
                        }

                        @media print {
                            html, body {
                                margin: 0 !important;
                                padding: 0 !important;
                                background: #fff !important;
                                height: auto !important;
                                overflow: visible !important;
                            }

                            .hbl-print-doc {
                                width: 210mm !important;
                                min-height: 297mm !important;
                                padding: 8mm !important;
                                margin: 0 !important;
                                box-shadow: none !important;
                                background: #fff !important;
                                box-sizing: border-box !important;
                                page-break-inside: auto;
                                display: flex !important;
                                flex-direction: column !important;
                            }

                            .hbl-print-doc,
                            .hbl-print-doc *,
                            .hbl-print-doc *::before,
                            .hbl-print-doc *::after {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }

                            .watermark { display: none !important; }
                        }

                        .hbl-print-doc .bol-value {
                            color: #000 !important;
                            font-weight: 700 !important;
                            font-size: 10px !important;
                            letter-spacing: 0.02em;
                            text-transform: uppercase;
                            line-height: 1.3;
                        }

                        .hbl-print-doc .hbl-paragraph,
                        .hbl-print-doc .hbl-paragraph * {
                            text-transform: none !important;
                            font-weight: 400 !important;
                            letter-spacing: normal !important;
                        }

                        .hbl-print-doc .text-xs {
                            color: #1f2937;
                            font-size: 11px;
                            line-height: 1.3;
                        }

                        .hbl-print-doc .hbl-charges-table {
                            width: 100%;
                            border-collapse: collapse;
                            table-layout: fixed;
                            margin: 0;
                        }

                        .hbl-print-doc .hbl-charges-table th,
                        .hbl-print-doc .hbl-charges-table td {
                            border: 1px solid #000;
                            padding: 3px 6px;
                            vertical-align: middle;
                            line-height: 1.25;
                            text-align: center;
                        }

                        .hbl-print-doc .hbl-charges-table thead th {
                            background-color: #d9d9d9;
                            color: #000;
                            font-weight: 800;
                            text-transform: uppercase;
                            letter-spacing: 0.03em;
                            font-size: 9px;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        .hbl-print-doc .hbl-charges-table tbody td {
                            color: #1f2937;
                            font-size: 10px;
                        }

                        .hbl-print-doc .hbl-charges-table tbody td:first-child {
                            text-align: left;
                        }

                        .hbl-print-doc .hbl-charges-table tfoot td {
                            background-color: #f3f3f3;
                            color: #000;
                            font-weight: 800;
                            font-size: 11px;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }

                        .hbl-print-doc .hbl-charges-table tfoot td:first-child {
                            text-align: right;
                            text-transform: uppercase;
                            letter-spacing: 0.02em;
                        }

                        .hbl-print-doc .hbl-charges-empty td {
                            height: 18px;
                            border-left: 1px solid #000;
                            border-right: 1px solid #000;
                            border-top: none;
                            border-bottom: none;
                        }

                        .hbl-print-header-block {
                            padding-bottom: 6px;
                        }

                        .hbl-print-doc .min-h-\\[70px\\]  { min-height: 50px !important; }
                        .hbl-print-doc .min-h-\\[100px\\] { min-height: 60px !important; }
                        .hbl-print-doc .min-h-\\[150px\\] { min-height: 70px !important; }
                        .hbl-print-doc .min-h-\\[280px\\] { min-height: 120px !important; }
                        .hbl-print-doc .min-h-\\[40px\\]  { min-height: 26px !important; }
                        .hbl-print-doc .mb-10 { margin-bottom: 1rem !important; }
                        .hbl-print-doc .mt-6  { margin-top: 0.5rem !important; }
                        .hbl-print-doc .mb-6  { margin-bottom: 0.25rem !important; }
                        .hbl-print-doc .mb-2  { margin-bottom: 0.15rem !important; }

                        @media print {
                            .hbl-print-doc .bol-value {
                                color: #000 !important;
                                font-weight: 700 !important;
                                text-transform: uppercase !important;
                            }
                            .hbl-print-doc .hbl-paragraph,
                            .hbl-print-doc .hbl-paragraph * {
                                text-transform: none !important;
                                font-weight: 400 !important;
                                letter-spacing: normal !important;
                            }

                            .hbl-print-doc .hbl-charges-table thead th,
                            .hbl-print-doc .hbl-charges-table tfoot td,
                            .hbl-print-doc .hbl-charges-table thead th *,
                            .hbl-print-doc .hbl-charges-table tfoot td * {
                                -webkit-print-color-adjust: exact !important;
                                print-color-adjust: exact !important;
                                color-adjust: exact !important;
                            }

                            .hbl-print-doc .hbl-charges-table,
                            .hbl-print-doc .hbl-bottom-section {
                                page-break-inside: avoid;
                                break-inside: avoid;
                            }

                            .hbl-print-doc .border,
                            .hbl-print-doc .border-t,
                            .hbl-print-doc .border-b,
                            .hbl-print-doc .border-l,
                            .hbl-print-doc .border-r {
                                border-style: solid !important;
                            }
                            .hbl-print-doc .border-black,
                            .hbl-print-doc .border-t.border-black,
                            .hbl-print-doc .border-b.border-black,
                            .hbl-print-doc .border-l.border-black,
                            .hbl-print-doc .border-r.border-black {
                                border-color: #000 !important;
                            }
                        }
                    `}</style>

                    {/* ── HEADER ── */}
                    <div className="hbl-print-header-block flex flex-col sm:flex-row justify-between items-start mb-2 gap-2">
                        <div className="flex items-center">
                            <div>
                                <img
                                    src={MasterCompanyDetails?.[0]?.CompanyLogoDark || ""}
                                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                                    className="h-10 sm:h-12 w-auto"
                                />
                            </div>
                        </div>
                        <h1 className="text-2xl sm:text-4xl font-bold text-primary tracking-wide">
                            BILL OF LADING
                        </h1>
                    </div>

                    {/* ── CONTENT (flex:1 pushes footer to bottom) ── */}
                    <div style={{ flex: 1 }}>
                        {/* Main Grid */}
                        <div className="border border-black hbl-main-grid">
                            {/* Row 1 */}
                            <div className="grid grid-cols-12">

                                <div className="col-span-6 border-r border-black">
                                    <div className="p-1 min-h-[70px]">
                                        <div className='bol-value'>SHIPPER/ EXPORTER (COMPLETE NAME AND ADDRESS)</div>
                                        <div className='text-xs'>{formData?.Shipper || ''}<br /></div>
                                        <div className='text-xs'>
                                            {formData?.ShipperAddress
                                                ? formData.ShipperAddress.split('\r\n').map((line, index) => (
                                                    <span key={index}>{line}<br /></span>
                                                ))
                                                : <span> </span>
                                            }
                                        </div>
                                    </div>
                                </div>

                                <div className="col-span-6">
                                    <div className="grid grid-cols-6 border-b border-black">
                                        <div className="col-span-3 p-1 border-r border-black">
                                            <div className='bol-value'>BOOKING NUMBER</div>
                                            <div className="text-xs">
                                                {formData?.CarrierBookingNo || ''}
                                            </div>
                                        </div>
                                        <div className="col-span-3 p-1">
                                            <div className='bol-value'>B/L NUMBER</div>
                                            <div className='text-xs'>{formData?.HBLNumber || ' '}</div>
                                        </div>
                                    </div>
                                    <div className="p-1">
                                        <div className='bol-value'>EXPORT REFERENCES</div>
                                        <div className="text-xs">
                                            CUST. REF. #: {formData?.CustomerPoNo || ' '}<br />
                                            {formData?.Coloader
                                                ? `CO-LOADER: ${formData.Coloader}`
                                                : ''}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2 - Consignee & Forwarding Agent */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-6 p-1 border-r border-black">
                                    <div className="bol-value">CONSIGNEE (NOT NEGOTIABLE UNLESS CONSIGNED TO ORDER) </div>
                                    <div className="text-xs ">
                                        {formData?.Consignee || ' '}<br />
                                        {formData?.ConsigneeAddress ? (
                                            formData?.ConsigneeAddress.split('\r\n').map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))
                                        ) : (
                                            <>
                                                ''
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-6">
                                    <div className="bol-value p-1">FORWARDING AGENT - F.M.C No.</div>
                                    <div className="p-1 text-xs">
                                        {formData?.ForwardingAgent && (
                                            <div>{formData.ForwardingAgent}</div>
                                        )}

                                        {formData?.ForwardingAgentAddress
                                            ?.split(/\r?\n/)
                                            .map((line, index) => (
                                                <div key={index}>{line}</div>
                                            ))}
                                    </div>
                                </div>
                            </div>

                            {/* Row 3 - Country of Origin */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-6 border-r border-black">
                                    <div className="col-span-6">
                                        <div className="bol-value p-1">NOTIFY (COMPLETE NAME AND ADDRESS)</div>
                                        <div className="p-2 text-xs min-h-[40px]">
                                            {formData?.Notify || ''}<br />
                                            {formData?.NotifyAddress ? (
                                                formData?.NotifyAddress.split('\r\n').map((line, index) => (
                                                    <span key={index}>{line}<br /></span>
                                                ))
                                            ) : (
                                                <span> </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-span-6 border-t border-black grid grid-cols-2 gap-0">
                                        <div className='border-r border-black p-1'>
                                            <div className="bol-value">
                                                PRE-CARRIAGE BY
                                            </div>
                                            <div className="text-xs">
                                                {formData?.PreCarrageBy || ''}
                                            </div>
                                        </div>

                                        <div className='p-1'>
                                            <div className="bol-value">
                                                PLACE OF RECEIPT
                                            </div>
                                            <div className="text-xs">
                                                {formData?.PalceOfReceipt || ''}
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div className="col-span-6">
                                    <div className='border-b border-black p-1'>
                                        <div className="bol-value">COUNTRY OF ORIGIN</div>
                                        <div className='text-xs'>
                                            {formData?.CountryOfOrigin || ''}
                                        </div>
                                    </div>
                                    <div className='p-1'>
                                        <div className="bol-value">FOR DELIVERY PLEASE CONTACT</div>
                                        <div className="text-xs min-h-[40px]">
                                            {formData?.DeliveryAgent || ''}<br />
                                            {formData?.DeliveryAgentAddress ? (
                                                formData?.DeliveryAgentAddress.split('\r\n').map((line, index) => (
                                                    <span key={index}>{line}<br /></span>
                                                ))
                                            ) : (
                                                <span> </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 6 - Ocean Vessel */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-4 p-1 border-r border-black">
                                    <div className="bol-value">VESSEL / VOYAGE</div>
                                    <div className="text-xs">
                                        {formData?.VesselName || formData?.Voyage ? (
                                            `${formData?.VesselName || ''}${formData?.VesselName && formData?.Voyage ? ' / ' : ''}${formData?.Voyage || ''}`
                                        ) : (
                                            '\u00A0'
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-4 p-1 border-r border-black">
                                    <div className="bol-value">PORT OF LOADING</div>
                                    <div className="text-xs">
                                        {formData?.PortOfLoading?.split('[')[0].trim() || ' '}
                                    </div>
                                </div>
                                <div className="col-span-4 p-1">
                                    <div className="text-xs">
                                        <div className='bol-value'>LOADING PIER / TERMINAL</div>
                                        {formData?.LoadingPierTerminal || '-'}
                                    </div>
                                </div>
                            </div>

                            {/* Row 7 - Port of Discharge */}
                            <div className="grid grid-cols-12 border-t border-black">
                                <div className="col-span-4 p-1 border-r border-black">
                                    <div className="bol-value">PORT OF DISCHARGE</div>
                                    <div className="text-xs">
                                        {formData?.PortOfDischarge?.split('[')[0].trim() || ' '}
                                    </div>
                                </div>
                                <div className="col-span-4 p-1 border-r border-black">
                                    <div className="bol-value">PLACE OF DELIVERY</div>
                                    <div className="text-xs">
                                        {formData?.PlaceOfDelivery || ''}
                                    </div>
                                </div>
                                <div className="col-span-4 p-1">
                                    <div className="bol-value">MOVE TYPE</div>
                                    <div className="text-xs">
                                        {formData?.MoveType || ''}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Cargo Details Header */}
                        <div className='p-1 bol-value text-center'>PARTICULARS FURNISHED BY SHIPPER</div>
                        <div className='border border-black'>

                            <div className="grid grid-cols-12 border-b border-black">
                                <div className="col-span-2 border-r border-black">
                                    <div className=" text-center p-1 bol-value ">
                                        MARKS & Nos
                                    </div>
                                </div>
                                <div className="col-span-1 border-r border-black">
                                    <div className=" text-center p-1 bol-value ">
                                        #PKGS
                                    </div>
                                </div>
                                <div className="col-span-5 border-r border-black">
                                    <div className=" text-center p-1 bol-value ">
                                        DESCRIPTION OF GOODS
                                    </div>
                                </div>
                                <div className="col-span-2 border-r border-black">
                                    <div className=" text-center p-1 bol-value ">GROSS WEIGHT</div>
                                </div>
                                <div className="col-span-2">
                                    <div className=" text-center p-1 bol-value ">MEASUREMENT</div>
                                </div>
                            </div>

                            {/* Cargo Details Body with Watermark */}
                            <div className="relative min-h-[100px]">
                                <div className="min-h-[100px]">
                                    {formData?.ContainersCargoModel?.map((cargo, index) => (
                                        <div
                                            key={cargo.ID || index}
                                            className="grid grid-cols-12"
                                        >
                                            {/* MARKS / CONTAINER / SEAL */}
                                            <div className="col-span-2 border-r border-black border-dashed">
                                                <div className="p-1 text-xs">
                                                    {cargo.MARKS_NO}<br />
                                                    {cargo.CONTAINER_NUMBER} / {cargo.SEAL_NUMBER}
                                                </div>
                                            </div>

                                            {/* CONTAINER TYPE */}
                                            <div className="col-span-1 border-r border-black border-dashed">
                                                <div className="p-1 text-xs text-center">
                                                    {cargo.CONT_SIZE} {cargo.CONT_TYPE}
                                                </div>
                                            </div>

                                            {/* DESCRIPTION */}
                                            <div className="col-span-5 border-r border-black border-dashed">
                                                <div className="p-1 text-xs">
                                                    {cargo.DESCRIPTIONS}<br />
                                                    {cargo.SCHEDULE_B && (
                                                        <>
                                                            <strong>Schedule B:</strong> {cargo.SCHEDULE_B}<br />
                                                        </>
                                                    )}

                                                    {cargo.NO_PACKAGE > 0 && (
                                                        <>
                                                            <strong>Packages:</strong> {cargo.NO_PACKAGE} {cargo.PACKAGE_CODE}<br />
                                                            <strong>Package Desc:</strong> {cargo.PACKAGE_DESCRIPTION}<br />
                                                        </>
                                                    )}

                                                    {cargo.HAZ_CODE && (
                                                        <div className="mt-2">
                                                            <strong>HAZARDOUS:</strong> Yes<br />
                                                            <strong>HAZ CODE:</strong> {cargo.HAZ_CODE}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* GROSS WEIGHT */}
                                            <div className="col-span-2 border-r border-black border-dashed">
                                                <div className="p-1 text-xs flex justify-center">
                                                    {cargo.KGS ? `${cargo.KGS} KGS` : ""} <br />
                                                    {cargo.LBS ? `${cargo.LBS} LBS` : ""}
                                                </div>
                                            </div>

                                            {/* MEASUREMENT */}
                                            <div className="col-span-2">
                                                <div className="p-1 text-xs flex items-center justify-center">
                                                    {cargo.CBM ? `${cargo.CBM} CBM` : ""}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {(!formData?.ContainersCargoModel || formData.ContainersCargoModel.length === 0) && (
                                        <div className="grid grid-cols-12 min-h-[280px]">
                                            <div className="col-span-2 border-r border-black border-dashed">
                                                <div className="p-2 bol-value h-full">&nbsp;</div>
                                            </div>
                                            <div className="col-span-1 border-r border-black border-dashed">
                                                <div className="p-2 bol-value h-full">&nbsp;</div>
                                            </div>
                                            <div className="col-span-5 border-r border-black border-dashed">
                                                <div className="p-2 bol-value h-full">&nbsp;</div>
                                            </div>
                                            <div className="col-span-2 border-r border-black border-dashed">
                                                <div className="p-2 bol-value h-full">&nbsp;</div>
                                            </div>
                                            <div className="col-span-2">
                                                <div className="p-2 bol-value h-full">&nbsp;</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Watermark */}
                                <div
                                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                                    style={{
                                        color: "hsl(220, 60%, 80%)",
                                        fontSize: "64px",
                                        fontWeight: "bold",
                                        letterSpacing: "6px",
                                        transform: "rotate(-25deg)",
                                        opacity: 0.4,
                                    }}
                                >
                                    DRAFT-COPY
                                </div>
                            </div>
                            <div className='grid grid-cols-12 border-y border-black'>
                                <div className='col-span-7 p-1 border-r border-black text-[11px]'>
                                    <div>SHIPPERS DECLARED VALUE $</div>
                                    <div>SUBJECT TO EXTRA FRIGHT AS PER TARRIF AND TERMS AND CONDITIONS OF THIS B/L</div>
                                </div>
                                <div className='col-span-5'>
                                    <div className='grid grid-cols-5'>
                                        <div className="col-span-3 border-r border-black">
                                            <div className="bol-value p-1">FRIGHT PAYABLE AT</div>
                                            <div className="text-xs text-center">
                                                {formData?.FreightPayable || '-'}
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="bol-value p-1">NO. OF ORIGINAL(S)</div>
                                            <div className="text-xs text-center">
                                                {formData?.NoOriginal || '-'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Bottom Section */}
                            <div className="grid grid-cols-12 hbl-bottom-section">
                                {/* Left - Freight Section */}
                                <div className="col-span-7 border-r border-black flex flex-col justify-between">

                                    {/* === CHARGES TABLE === */}
                                    <table className="hbl-charges-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '50%' }}>DESCRIPTION OF CHARGES</th>
                                                <th style={{ width: '25%' }}>PREPAID (USD)</th>
                                                <th style={{ width: '25%' }}>COLLECT (USD)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {groupedCharges?.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.description}</td>
                                                    <td>{item.prepaid ? item.prepaid.toFixed(2) : ""}</td>
                                                    <td>{item.collect ? item.collect.toFixed(2) : ""}</td>
                                                </tr>
                                            ))}
                                            {(!groupedCharges || groupedCharges.length === 0) && (
                                                <tr>
                                                    <td colSpan={3} style={{ height: '60px' }}>&nbsp;</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td>GRAND TOTAL</td>
                                                <td>
                                                    {groupedCharges
                                                        ?.reduce((sum, i) => sum + (i.prepaid || 0), 0)
                                                        .toFixed(2)}
                                                </td>
                                                <td>
                                                    {groupedCharges
                                                        ?.reduce((sum, i) => sum + (i.collect || 0), 0)
                                                        .toFixed(2)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>

                                    <div className="grid grid-cols-2 border-t border-black">
                                        <div className="p-1 flex items-center gap-1 border-r border-black">
                                            <div className='bol-value'>PLACE OF ISSUE:</div>
                                            <span className="text-xs">{formData?.PortOfLoading?.split('[')[0].trim() || ''}</span>
                                        </div>
                                        <div className="p-1 flex items-center gap-1">
                                            <div className='bol-value'>DATE:</div>
                                            <div className="text-xs">{formatDate(formData?.CreatedOn) || ''}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right - Terms & Signature */}
                                <div className="col-span-5 flex flex-col justify-between">
                                    <div className="hbl-paragraph p-2 leading-tight" style={{ minHeight: "70px" }}>
                                        <div className='text-[9px] leading-tight text-justify text-gray-800'>RECEIVED by the carrier the goods as specified above in apparent good order and condition unless otherwise stated, to be transported to such place as agreed, authorized or permitted herein and subject to all the terms and conditions appearing on the front and reverse of this bill of lading to which the Merchant agrees by accepting this bill of lading, and local privileges and customers not withstanding. The particulars given above as stated by the shipper and the weight, measure, quantity, condition, contents and value of the goods are unknown to the Carrier. In WITNESS whereof one (1) original bill of lading has been signed if not otherwise stated above, the same being accomplished the other(s), if any to be void. If required by the carrier one (1) original Bill of Lading must be surrendered duly endorsed in exchange for the goods or delivery order.</div>
                                    </div>
                                    <div className='text-center'>
                                        <div className="text-xs underline text-gray-700">
                                            Signed on behalf of the carrier
                                        </div>
                                        <div className='mb-10 text-md'>
                                            {MasterCompanyDetails?.[0]?.CompanyName || 'www.infox.com'}
                                        </div>
                                        <div>
                                            By
                                            <span className="inline-block border-b border-black w-48 ml-2"></span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* ── END CONTENT ── */}

                    {/* ── FOOTER — always at bottom of A4 page ── */}
                    <div className="text-[8px] text-muted-foreground mt-1">
                        Powered by {MasterCompanyDetails?.[0]?.PoweredBy || 'www.infox.com'}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HBLPrint;